import {
    SupportFeedCommentPageDto,
    SupportFeedEntryDto,
    SupportFeedPageDto
} from '@api/ChallengeClient';
import { useError } from '@components/hooks/useError';
import { Api } from '@core/api';
import { toast } from '@core/toast';
import { injectTSDI } from '@core/tsdi';
import { endOfDay, startOfDay } from 'date-fns';
import React from 'react';
import {
    CommentStateMap,
    commentStateReducer
} from './comment-state-reducer';
import { collectImageUrls, csvEscape } from './support-feed-helpers';

export type EntityType = 'community' | 'team';

const PAGE_SIZE = 100;

export function useSupportFeedStore(
    entityId: number,
    entityType: EntityType
) {
    const { challengeClient } = injectTSDI(Api);
    const showError = useError();

    const [draftFrom, setDraftFrom] = React.useState<Date | null>(null);
    const [draftTo, setDraftTo] = React.useState<Date | null>(null);
    const [appliedFrom, setAppliedFrom] = React.useState<
        string | undefined
    >();
    const [appliedTo, setAppliedTo] = React.useState<string | undefined>();

    const [entries, setEntries] = React.useState<SupportFeedEntryDto[]>([]);
    const [hasMore, setHasMore] = React.useState(false);
    const [next, setNext] = React.useState<string | undefined>();

    const [loading, setLoading] = React.useState(false);
    const [loadingMore, setLoadingMore] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [pinUpdatingId, setPinUpdatingId] = React.useState<
        string | undefined
    >();

    const [commentStateById, dispatchComment] = React.useReducer(
        commentStateReducer,
        {} as CommentStateMap
    );
    const commentStateRef = React.useRef(commentStateById);
    commentStateRef.current = commentStateById;

    const [selectedIds, setSelectedIds] = React.useState<
        Record<string, boolean>
    >({});

    const selectedEntries = React.useMemo(
        () => entries.filter((entry) => selectedIds[entry.id]),
        [entries, selectedIds]
    );

    const loadedEntryCount = entries.length;
    const selectedLoadedEntryCount = selectedEntries.length;
    const allLoadedEntriesSelected =
        loadedEntryCount > 0 && selectedLoadedEntryCount === loadedEntryCount;
    const someLoadedEntriesSelected =
        selectedLoadedEntryCount > 0 &&
        selectedLoadedEntryCount < loadedEntryCount;

    // Refs for values read inside async callbacks to keep callbacks stable
    const appliedFromRef = React.useRef(appliedFrom);
    appliedFromRef.current = appliedFrom;
    const appliedToRef = React.useRef(appliedTo);
    appliedToRef.current = appliedTo;
    const showErrorRef = React.useRef(showError);
    showErrorRef.current = showError;
    const nextRef = React.useRef(next);
    nextRef.current = next;

    // --- API calls (stable — read volatile values from refs) ---

    const fetchFeedPage = React.useCallback(
        async (cursor?: string): Promise<SupportFeedPageDto> => {
            const query = {
                limit: PAGE_SIZE,
                ...(cursor ? { next: cursor } : {}),
                ...(appliedFromRef.current
                    ? { fromCreatedAt: appliedFromRef.current }
                    : {}),
                ...(appliedToRef.current
                    ? { toCreatedAt: appliedToRef.current }
                    : {})
            };

            if (entityType === 'community') {
                const response =
                    await challengeClient.support.communityFeedSupportControllerGetFeed(
                        entityId,
                        query
                    );
                return response.data;
            }

            const response =
                await challengeClient.support.teamFeedSupportControllerGetFeed(
                    entityId,
                    query
                );
            return response.data;
        },
        [challengeClient, entityId, entityType]
    );

    const fetchCommentsPage = React.useCallback(
        async (
            activityId: string,
            cursor?: string
        ): Promise<SupportFeedCommentPageDto> => {
            const query = {
                limit: PAGE_SIZE,
                ...(cursor ? { next: cursor } : {})
            };

            if (entityType === 'community') {
                const response =
                    await challengeClient.support.communityFeedSupportControllerGetFeedComments(
                        entityId,
                        activityId,
                        query
                    );
                return response.data;
            }

            const response =
                await challengeClient.support.teamFeedSupportControllerGetFeedComments(
                    entityId,
                    activityId,
                    query
                );
            return response.data;
        },
        [challengeClient, entityId, entityType]
    );

    // --- Feed loading ---

    const resetClientState = React.useCallback(() => {
        dispatchComment({ type: 'RESET' });
        setEntries([]);
        setHasMore(false);
        setNext(undefined);
        setSelectedIds({});
    }, []);

    const loadInitialFeed = React.useCallback(async () => {
        setLoading(true);
        setError(null);
        resetClientState();

        try {
            const response = await fetchFeedPage();
            setEntries(response.items || []);
            setHasMore(response.hasMore);
            setNext(response.next);
        } catch (requestError) {
            showErrorRef.current(requestError);
            setError('Failed to load feed entries.');
        } finally {
            setLoading(false);
        }
    }, [fetchFeedPage, resetClientState]);

    React.useEffect(() => {
        void loadInitialFeed();
    }, [loadInitialFeed, appliedFrom, appliedTo]);

    const handleLoadMore = React.useCallback(async () => {
        const cursor = nextRef.current;
        if (!cursor) {
            return;
        }

        setLoadingMore(true);
        try {
            const response = await fetchFeedPage(cursor);
            setEntries((current) => [
                ...current,
                ...(response.items || [])
            ]);
            setHasMore(response.hasMore);
            setNext(response.next);
        } catch (requestError) {
            showErrorRef.current(requestError);
            setError('Failed to load more feed entries.');
        } finally {
            setLoadingMore(false);
        }
    }, [fetchFeedPage]);

    const setEntryPinned = React.useCallback(
        async (activityId: string, pinned: boolean) => {
            const support = challengeClient.support;
            if (entityType === 'community') {
                await (pinned
                    ? support.communityFeedSupportControllerPinFeedActivity(
                          entityId,
                          activityId
                      )
                    : support.communityFeedSupportControllerUnpinFeedActivity(
                          entityId,
                          activityId
                      ));
                return;
            }

            await (pinned
                ? support.teamFeedSupportControllerPinFeedActivity(
                      entityId,
                      activityId
                  )
                : support.teamFeedSupportControllerUnpinFeedActivity(
                      entityId,
                      activityId
                  ));
        },
        [challengeClient, entityId, entityType]
    );

    const handleTogglePinned = React.useCallback(
        async (entry: SupportFeedEntryDto) => {
            setPinUpdatingId(entry.id);
            try {
                await setEntryPinned(entry.id, !entry.pinned);
                toast.success(entry.pinned ? 'Post unpinned' : 'Post pinned');
                await loadInitialFeed();
            } catch (requestError) {
                if (
                    !entry.pinned &&
                    (requestError as { status?: number }).status === 400
                ) {
                    toast.error('Pin limit reached. Unpin another post first.');
                    return;
                }
                showErrorRef.current(requestError);
            } finally {
                setPinUpdatingId(undefined);
            }
        },
        [setEntryPinned, loadInitialFeed]
    );

    // --- Comments ---

    const loadComments = React.useCallback(
        async (activityId: string, append: boolean) => {
            const cursor = append
                ? commentStateRef.current[activityId]?.next
                : undefined;

            dispatchComment(
                append
                    ? { type: 'LOAD_MORE_START', activityId }
                    : { type: 'LOAD_START', activityId }
            );

            try {
                const response = await fetchCommentsPage(
                    activityId,
                    cursor
                );
                dispatchComment({
                    type: 'LOAD_SUCCESS',
                    activityId,
                    items: response.items || [],
                    hasMore: response.hasMore,
                    next: response.next,
                    append
                });
            } catch (requestError) {
                showErrorRef.current(requestError);
                dispatchComment({ type: 'LOAD_ERROR', activityId });
            }
        },
        [fetchCommentsPage]
    );

    const handleToggleComments = React.useCallback(
        async (entry: SupportFeedEntryDto) => {
            const currentState =
                commentStateRef.current[entry.id];

            if (currentState?.loaded) {
                dispatchComment({
                    type: 'TOGGLE_EXPANDED',
                    activityId: entry.id
                });
                return;
            }

            if ((entry.commentCount || 0) === 0) {
                return;
            }

            await loadComments(entry.id, false);
        },
        [loadComments]
    );

    const handleCommentLoadMore = React.useCallback(
        async (activityId: string) => {
            await loadComments(activityId, true);
        },
        [loadComments]
    );

    // --- Filters ---

    const handleApplyFilters = React.useCallback(() => {
        setAppliedFrom(
            draftFrom ? startOfDay(draftFrom).toISOString() : undefined
        );
        setAppliedTo(
            draftTo ? endOfDay(draftTo).toISOString() : undefined
        );
    }, [draftFrom, draftTo]);

    const handleClearFilters = React.useCallback(() => {
        setDraftFrom(null);
        setDraftTo(null);
        setAppliedFrom(undefined);
        setAppliedTo(undefined);
    }, []);

    // --- Selection ---

    const handleSelectionChange = React.useCallback(
        (entryId: string) => {
            setSelectedIds((current) => ({
                ...current,
                [entryId]: !current[entryId]
            }));
        },
        []
    );

    const handleSelectLoadedEntries = React.useCallback(() => {
        setSelectedIds((current) => ({
            ...current,
            ...entries.reduce<Record<string, boolean>>((result, entry) => {
                result[entry.id] = true;
                return result;
            }, {})
        }));
    }, [entries]);

    const handleClearSelection = React.useCallback(() => {
        setSelectedIds({});
    }, []);

    // --- Export ---

    const handleExport = React.useCallback(() => {
        const rows = selectedEntries.map((entry) => ({
            entityType,
            entityId: `${entityId}`,
            activityId: entry.id,
            createdDate: entry.createdDate || '',
            authorUserId: entry.author?.userId || '',
            authorStreamUserId: entry.author?.streamUserId || '',
            authorName: entry.author?.name || '',
            text: entry.text || '',
            mentionedUserIds: entry.mentionedUsers
                .map((user) => user.userId || user.streamUserId)
                .join(';'),
            attachmentTypes: entry.attachments
                .map((attachment) => attachment.type || 'unknown')
                .join(';'),
            attachmentSummary: entry.attachments
                .map((attachment) =>
                    [attachment.type, attachment.title, attachment.text]
                        .filter(Boolean)
                        .join(' | ')
                )
                .join(' || '),
            imageUrls: collectImageUrls(entry.attachments).join(';'),
            commentCount: `${entry.commentCount || 0}`,
            topicModerationApproved:
                entry.topicModerationApproved === undefined
                    ? ''
                    : String(entry.topicModerationApproved)
        }));

        const csv = [
            [
                'entityType',
                'entityId',
                'activityId',
                'createdDate',
                'authorUserId',
                'authorStreamUserId',
                'authorName',
                'text',
                'mentionedUserIds',
                'attachmentTypes',
                'attachmentSummary',
                'imageUrls',
                'commentCount',
                'topicModerationApproved'
            ].join(','),
            ...rows.map((row) =>
                [
                    row.entityType,
                    row.entityId,
                    row.activityId,
                    row.createdDate,
                    row.authorUserId,
                    row.authorStreamUserId,
                    row.authorName,
                    row.text,
                    row.mentionedUserIds,
                    row.attachmentTypes,
                    row.attachmentSummary,
                    row.imageUrls,
                    row.commentCount,
                    row.topicModerationApproved
                ]
                    .map(csvEscape)
                    .join(',')
            )
        ].join('\n');

        const blob = new Blob([csv], { type: 'text/csv' });
        const csvUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = csvUrl;
        link.setAttribute(
            'download',
            `${entityType}-feed-${entityId}-selection.csv`
        );
        link.click();
        window.URL.revokeObjectURL(csvUrl);
    }, [entityId, entityType, selectedEntries]);

    return {
        entries,
        loading,
        loadingMore,
        error,
        hasMore,

        draftFrom,
        draftTo,
        setDraftFrom,
        setDraftTo,
        handleApplyFilters,
        handleClearFilters,

        selectedIds,
        selectedEntries,
        loadedEntryCount,
        selectedLoadedEntryCount,
        allLoadedEntriesSelected,
        someLoadedEntriesSelected,
        handleSelectionChange,
        handleSelectLoadedEntries,
        handleClearSelection,

        commentStateById,
        handleToggleComments,
        handleCommentLoadMore,

        pinUpdatingId,
        handleTogglePinned,

        handleLoadMore,
        handleExport
    };
}
